import apiClient from "../../core/apiClient";
import { PolicyInsightsRuleBased } from "../../interfaces/DTO/insights/PolicyInsightsRuleBased";
import { RuleCopyDTO } from "../../interfaces/DTO/insights/RuleCopyDTO";
import { DoctrineBlockDTO } from "../../interfaces/DTO/insights/DoctrineBlockDTO";
import { BaseService } from "../BaseService";


const API_URL = "/policy-insights";

class PolicyInsightsRuleBasedService extends BaseService<PolicyInsightsRuleBased> {
  constructor() {
    super(API_URL);
  }
  /**
   * 🔍 שליפת כל הכללים לפי userId
   */
  async fetchAllByContactId(contact_id: number): Promise<PolicyInsightsRuleBased[]> {
    try {
      const response = await apiClient.get<PolicyInsightsRuleBased[]>(`${API_URL}/by-user/${contact_id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch policy insights for userId=${contact_id}:`, error);
      throw error;
    }
  }

 async fetchAll(reportedPeriod: string, isPreAdvice: boolean = false): Promise<PolicyInsightsRuleBased[]> {
  try {
    const response = await apiClient.get<PolicyInsightsRuleBased[]>(
      `${API_URL}/by-period`,
      { params: { reportedPeriod, isPreAdvice } }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to fetch all policy insights:", error);
    throw error;
  }
}

async fetchAllByContactIdAndPeriod(contactId: number, period: string): Promise<PolicyInsightsRuleBased[]> {
    try {
      const response = await apiClient.get<PolicyInsightsRuleBased[]>(
        `${API_URL}/by-period-and-contact`,
        {
          params: {
            contactId,
            reportedPeriod: period,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch policy insights for contactId=${contactId} and period=${period}:`, error);
      throw error;
    }
  }

  async fetchAllByContactIdsAndPeriod(contactIds: number[], period: string): Promise<PolicyInsightsRuleBased[]> {
    try {
      const response = await apiClient.post<PolicyInsightsRuleBased[]>(
        `${API_URL}/by-period-and-contacts`,
        contactIds,
        {
          params: {
            reportedPeriod: period,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch policy insights for contactIds=${contactIds.join(", ")} and period=${period}:`, error);
      throw error;
    }
  }
async fetchAllByContactIdsPreAdvice(contactIds: number[]): Promise<PolicyInsightsRuleBased[]> {
    try {
      const response = await apiClient.post<PolicyInsightsRuleBased[]>(
        `${API_URL}/by-contacts-pre-advice`,
        contactIds
      );
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to fetch pre-advice insights for contactIds=${contactIds.join(", ")}:`, error);
      throw error;
    }
  }

async analyzeByPeriodAndContacts(
  contactIds: number[],
  reportedPeriod: string,
  save: boolean = false
): Promise<PolicyInsightsRuleBased[]> {
  try {
    const response = await apiClient.post<PolicyInsightsRuleBased[]>(
      `${API_URL}/analyze/by-period-and-contacts`,
      contactIds,
      {
        params: {
          reportedPeriod,
          save,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      `❌ Failed to analyze policy insights for contacts=${contactIds.join(", ")} and period=${reportedPeriod}:`,
      error
    );
    throw error;
  }
}
async analyzeCustomMimshaks(
  mimshaks: any[],
  reportedPeriod: string
): Promise<PolicyInsightsRuleBased[]> {
  try {
    const response = await apiClient.post<PolicyInsightsRuleBased[]>(
      `${API_URL}/analyze/custom-mimshaks`,
      mimshaks,
      {
        params: { reportedPeriod },
      }
    );
    return response.data;
  } catch (error) {
    console.error("❌ Failed to analyze custom mimshaks:", error);
    throw error;
  }
}

async bulkDeleteByPeriod(reportedPeriod: string): Promise<{ deleted: number }> {
  try {
    const response = await apiClient.delete<{ deleted: number }>(
      `${API_URL}/bulk-delete/by-period`,
      { params: { reportedPeriod } }
    );
    return response.data;
  } catch (error) {
    console.error(`❌ Failed to bulk delete insights for period=${reportedPeriod}:`, error);
    throw error;
  }
}

async bulkDeletePreAdviceByContactIds(
  contactIds: number[]
): Promise<{ deleted: number }> {
  try {
    const response = await apiClient.post<{ deleted: number }>(
      `${API_URL}/bulk-delete/pre-advice-by-contacts`,
      contactIds
    );
    return response.data;
  } catch (error) {
    console.error(
      `❌ Failed to bulk delete pre-advice insights for contacts=${contactIds.join(", ")}:`,
      error
    );
    throw error;
  }
}

async bulkDeleteByContactsAndPeriod(
  contactIds: number[],
  reportedPeriod: string
): Promise<{ deleted: number }> {
  try {
    const response = await apiClient.post<{ deleted: number }>(
      `${API_URL}/bulk-delete/by-contacts-and-period`,
      contactIds,
      { params: { reportedPeriod } }
    );
    return response.data;
  } catch (error) {
    console.error(
      `❌ Failed to bulk delete insights for contacts=${contactIds.join(", ")} and period=${reportedPeriod}:`,
      error
    );
    throw error;
  }
}
async analyzePreAdviceForContacts(
  contactIds: number[],
  save: boolean = true
): Promise<PolicyInsightsRuleBased[]> {
  try {
    const response = await apiClient.post<PolicyInsightsRuleBased[]>(
      `${API_URL}/analyze/pre-advice-for-contacts`,
      contactIds,
      { params: { save } }
    );
    return response.data;
  } catch (error) {
    console.error(
      `❌ Failed to analyze pre-advice insights for contacts=${contactIds.join(", ")}:`,
      error
    );
    throw error;
  }
}

async fetchRuleTypeDescription(ruleType: string): Promise<RuleCopyDTO> {
  try {
    const response = await apiClient.get<RuleCopyDTO>(
      `${API_URL}/rule-type-description/${ruleType}`
    );
    return response.data;
  } catch (error) {
    console.error(`❌ Failed to fetch description for ruleType=${ruleType}:`, error);
    throw error;
  }
}

async fetchRuleCopyLibrary(): Promise<RuleCopyDTO[]> {
  const response = await apiClient.get<RuleCopyDTO[]>(`${API_URL}/rule-copy-library`);
  return response.data;
}

async fetchDoctrineLibrary(): Promise<DoctrineBlockDTO[]> {
  const response = await apiClient.get<DoctrineBlockDTO[]>(`${API_URL}/doctrine-library`);
  return response.data;
}

/** SUPERADMIN - the pre-advice insights of every client again (after fixing / adding a rule); monthly ones untouched */
async recalculateAllPreAdvice(): Promise<{ contacts: number; insights: number; failed_contacts: number[] }> {
  const response = await apiClient.post<{ contacts: number; insights: number; failed_contacts: number[] }>(
    `${API_URL}/analyze/pre-advice-all`
  );
  return response.data;
}

async analyzeForCompanyByPeriod(
  reportedPeriod: string
): Promise<{ company_id: number; reported_period: string; analyzed: number; errors: number }> {
  try {
    const response = await apiClient.post<
      { company_id: number; reported_period: string; analyzed: number; errors: number }
    >(`${API_URL}/analyze-for-company/by-period`, null, {
      params: { reportedPeriod },
    });
    return response.data;
  } catch (error) {
    console.error(
      `❌ Failed to analyze policy insights for reportedPeriod=${reportedPeriod}:`,
      error
    );
    throw error;
  }
}

 } 

  

// ✅ ייצוא המחלקה
export const policyInsightsRuleBasedService = new PolicyInsightsRuleBasedService();
